// Modální průvodce prací s okny. Okno = sdílený `Dialog` (chromeless režim → vlastní pixel-perfect
// chrome), tlačítka = sdílený `Button`. Stage (mini-shell schémata + animace) řídí imperativní engine
// portovaný 1:1 z návrhu; React vlastní jen hlavičku, info panel, patičku a navigaci kroků.

import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog';
import { cn } from '@/shared/lib/utils';

import {
  ICO_DETACH,
  ICO_DOCK,
  ONBOARDING_STEP_COUNT,
  createOnboardingEngine,
  type OnboardingEngine,
} from './windowOnboardingEngine';
import './windowOnboarding.css';

export type WindowOnboardingCloseReason = 'completed' | 'skipped';

export interface WindowOnboardingWizardProps {
  open: boolean;
  onClose: (reason: WindowOnboardingCloseReason) => void;
}

const N = ONBOARDING_STEP_COUNT;

/** Nahradí {detach}/{dock} tokeny inline SVG ikonami (stejné glyphy jako ve stage). */
function withIcons(text: string): string {
  return text
    .replace(/\{detach\}/g, `<span class="ob-ico">${ICO_DETACH}</span>`)
    .replace(/\{dock\}/g, `<span class="ob-ico">${ICO_DOCK}</span>`);
}

export function WindowOnboardingWizard({ open, onClose }: WindowOnboardingWizardProps) {
  const { t } = useTranslation('windowOnboarding');
  const [step, setStep] = useState(0);
  const scenesRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<OnboardingEngine | null>(null);

  // Při každém otevření začni od prvního kroku.
  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // Postav engine (a scény) jednou na otevření; ukliď při zavření.
  useEffect(() => {
    if (!open) return;
    const root = scenesRef.current;
    if (!root) return;
    const engine = createOnboardingEngine(root);
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
      root.innerHTML = '';
    };
  }, [open]);

  // Přepni a přehraj scénu aktivního kroku.
  useEffect(() => {
    if (!open) return;
    const engine = engineRef.current;
    if (!engine) return;
    engine.showScene(step);
    const id = window.setTimeout(() => engine.play(step), 60);
    return () => window.clearTimeout(id);
  }, [open, step]);

  // Šipky vlevo/vpravo přepínají kroky (Escape řeší Dialog → skip).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setStep((s) => Math.min(s + 1, N - 1));
      else if (e.key === 'ArrowLeft') setStep((s) => Math.max(s - 1, 0));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSkip = () => onClose('skipped');
  const handleNext = () => {
    if (step >= N - 1) onClose('completed');
    else setStep(step + 1);
  };

  const n = step + 1;
  const points = t(`steps.${n}.points`, { returnObjects: true }) as string[];
  const tip = t(`steps.${n}.tip`, { defaultValue: '' });
  const isLast = step === N - 1;

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      chromeless
      closeOnBackdrop={false}
      ariaLabel={t('brand')}
      className="border-phx-border-2 h-[min(560px,calc(100vh-48px))] max-h-[calc(100vh-48px)] w-[min(940px,100%)]"
    >
      <div className="phx-ob phx-root">
        {/* ---- Head ---- */}
        <header className="ob-head">
          <span className="ob-brand">
            <b>{t('brand')}</b>
          </span>
          <div className="ob-head-right">
            <span className="ob-counter">
              <Trans
                i18nKey="counter"
                ns="windowOnboarding"
                values={{ current: n, total: N }}
                components={{ b: <b /> }}
              />
            </span>
            <button
              type="button"
              className="ob-x"
              title={t('nav.closeTitle')}
              aria-label={t('nav.closeAria')}
              onClick={handleSkip}
            >
              <svg width="13" height="13" viewBox="0 0 12 12">
                <path
                  d="M3 3l6 6M9 3l-6 6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>

        {/* ---- Body: stage | info ---- */}
        <div className="ob-body">
          <div className="ob-stage">
            <div ref={scenesRef} className="ob-scenes" />
            <button
              type="button"
              className="ob-replay"
              onClick={() => engineRef.current?.play(step)}
            >
              <svg width="12" height="12" viewBox="0 0 12 12">
                <path
                  d="M10 6a4 4 0 1 1-1.2-2.85M10 1.6V4H7.6"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              {t('nav.replay')}
            </button>
          </div>

          <div className="ob-info">
            <div className="ob-eyebrow">{t(`steps.${n}.eyebrow`)}</div>
            <h2 className="ob-title">{t(`steps.${n}.title`)}</h2>
            {/* Texty jsou důvěryhodné překlady (markup <b>/<code> + ikony) → bezpečné vykreslit jako HTML. */}
            <p
              className="ob-text"
              dangerouslySetInnerHTML={{ __html: withIcons(t(`steps.${n}.text`)) }}
            />
            <ul className="ob-points">
              {points.map((p, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: withIcons(p) }} />
              ))}
            </ul>
            {tip ? (
              <div className="ob-tip" dangerouslySetInnerHTML={{ __html: withIcons(tip) }} />
            ) : null}
          </div>
        </div>

        {/* ---- Footer ---- */}
        <footer className="ob-foot">
          <div className="ob-dots">
            {Array.from({ length: N }, (_, k) => (
              <button
                key={k}
                type="button"
                className="ob-dot"
                data-on={k === step ? '' : undefined}
                data-done={k < step ? '' : undefined}
                title={t(`steps.${k + 1}.title`)}
                aria-label={t(`steps.${k + 1}.title`)}
                onClick={() => setStep(k)}
              />
            ))}
          </div>
          <div className="ob-nav">
            <Button variant="ghost" size="md" onClick={handleSkip}>
              {t('nav.skip')}
            </Button>
            <Button
              variant="default"
              size="md"
              className={cn(step === 0 && 'invisible')}
              onClick={() => setStep((s) => Math.max(s - 1, 0))}
            >
              {t('nav.back')}
            </Button>
            <Button variant="primary" size="md" onClick={handleNext}>
              {isLast ? t('nav.finish') : t('nav.next')}
            </Button>
          </div>
        </footer>
      </div>
    </Dialog>
  );
}
